import { colors, fonts, components, spacing } from '../theme';

const styles = {
  wrapper: {
    width: '100%',
    maxWidth: '100%',
    overflowX: 'auto',
    minWidth: 0,
  },
  table: {
    width: '100%',
    maxWidth: '100%',
    borderCollapse: 'collapse',
    backgroundColor: colors.white,
    tableLayout: 'fixed',
  },
  thead: {},
  tbody: {},
  tr: {
    transition: 'background-color 0.2s ease',
  },
  trHover: {
    backgroundColor: colors.gray50,
  },
  th: {
    ...components.tableHeaderCell,
  },
  td: {
    ...components.tableCell,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    wordBreak: 'break-word',
  },
  cardView: {},
  card: {
    backgroundColor: colors.white,
    border: `1px solid ${colors.gray200}`,
    borderRadius: '19px',
    padding: spacing.md,
    marginBottom: spacing.md,
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: spacing.sm,
  },
  cardTitle: {
    fontFamily: fonts.heading,
    fontWeight: 600,
    color: colors.darkGray,
    margin: 0,
  },
  cardRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: `${spacing.xs} 0`,
    borderBottom: `1px solid ${colors.gray200}`,
  },
  cardLabel: {
    fontFamily: fonts.heading,
    fontWeight: 600,
    color: colors.midGray,
    fontSize: '0.875rem',
  },
  cardValue: {
    color: colors.darkGray,
  },
};

function Table({
  columns,
  data,
  onRowClick = null,
  responsive = true,
  className = '',
  keyField = null,
  selectedKey = null,
  ...props
}) {
  const getRowKey = (row, rowIndex) =>
    keyField && row[keyField] != null ? row[keyField] : rowIndex;

  const isSelected = (row, rowIndex) =>
    selectedKey != null && getRowKey(row, rowIndex) === selectedKey;

  const renderCell = (row, column, index) => {
    if (column.render) {
      return column.render(row[column.key], row, index);
    }
    return row[column.key];
  };

  const renderCardView = () => {
    if (!responsive) return null;

    return (
      <div style={styles.cardView} className="table-card-view">
        {data.map((row, rowIndex) => (
          <div
            key={getRowKey(row, rowIndex)}
            style={{
              ...styles.card,
              ...(isSelected(row, rowIndex)
                ? { borderColor: colors.blue, backgroundColor: colors.gray50 }
                : {}),
              ...(onRowClick ? { cursor: 'pointer' } : {}),
            }}
            onClick={onRowClick ? () => onRowClick(row, rowIndex) : undefined}
          >
            <div style={styles.cardHeader}>
              {columns[0] && (
                <h3 style={styles.cardTitle}>{renderCell(row, columns[0], rowIndex)}</h3>
              )}
            </div>
            {columns.slice(1).map((column, colIndex) => (
              <div key={colIndex} style={styles.cardRow}>
                <span style={styles.cardLabel}>{column.label}:</span>
                <span style={styles.cardValue}>{renderCell(row, column, rowIndex)}</span>
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  };

  if (!data || data.length === 0) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: colors.midGray }}>
        No data to display
      </div>
    );
  }

  return (
    <div style={styles.wrapper}>
      <table style={styles.table} className={`${responsive ? 'table-desktop' : ''} ${className}`.trim()} {...props}>
        <thead style={styles.thead}>
          <tr>
            {columns.map((column, index) => (
              <th
                key={index}
                style={{
                  ...styles.th,
                  ...(column.width ? { width: column.width } : {}),
                }}
              >
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody style={styles.tbody}>
          {data.map((row, rowIndex) => {
            const selected = isSelected(row, rowIndex);
            return (
              <tr
                key={getRowKey(row, rowIndex)}
                style={{
                  ...styles.tr,
                  cursor: onRowClick ? 'pointer' : 'default',
                  backgroundColor: selected ? colors.gray50 : undefined,
                  boxShadow: selected ? `inset 3px 0 0 ${colors.blue}` : undefined,
                }}
                onClick={onRowClick ? () => onRowClick(row, rowIndex) : undefined}
                onMouseEnter={(e) => {
                  if (onRowClick && !selected) {
                    e.currentTarget.style.backgroundColor = styles.trHover.backgroundColor;
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = selected ? colors.gray50 : '';
                }}
              >
                {columns.map((column, colIndex) => (
                  <td
                    key={colIndex}
                    style={{
                      ...styles.td,
                      ...(column.width ? { width: column.width } : {}),
                    }}
                    title={
                      typeof row[column.key] === 'string' ? row[column.key] : undefined
                    }
                  >
                    {renderCell(row, column, rowIndex)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
      {renderCardView()}
      <style>{`
        .table-card-view {
          display: none;
        }
        @media (max-width: 768px) {
          .table-desktop {
            display: none !important;
          }
          .table-card-view {
            display: block !important;
          }
        }
        @media (min-width: 769px) {
          .table-card-view {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
}

export default Table;

